import { OverlayToggleProps, Popover, Tooltip } from '@sgme/ui';
import classNames from 'classnames';
import { ToggleButton } from 'components/shared/compact-selector/toggle-button';
import { LabelItem } from 'components/shared/compact-selector/types';
import { OverflowTrigger } from 'components/shared/overflow-trigger';
import React, { ReactNode, RefObject, useCallback, useState } from 'react';
import styled, { css } from 'styled-components';
import { mediaQueryEqualOrUpper } from 'utils/mediaQueries';

type OnItemChanged<Item extends LabelItem> = (value: Nullable<Item>) => void;

type CompactSelectorProps<Item extends LabelItem> = Readonly<{
  autoOpen?: boolean;
  className?: string;
  hasBorder?: boolean;
  hasClearButton?: boolean;
  popupPlacement?: OverlayToggleProps['placement'];
  // popupOffset?: OverlayToggleProps['offset'];
  // popupArrow?: OverlayToggleProps['arrow'];
  isDisabled?: boolean;
  isReadonly?: boolean;
  selectedValue?: Nullable<Item>;
  onSelectedItemChanged?: OnItemChanged<Item>;
  renderContent: (onSelected: OnItemChanged<Item>, selectedValue: Nullable<Item>) => ReactNode;
  tooltip?: string | undefined | React.JSX.Element;
  e2ePrefix?: string;
  openOnHover?: boolean;
  renderInlineWhenOpened?: boolean;
  renderButton: (ref: RefObject<HTMLButtonElement | null>, onClick: () => void) => ReactNode;
  onOpenChange?: (isOpen: boolean) => void;
}>;

const Backdrop = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  opacity: 1;
  z-index: 1000;
`;

const StyledCardDiv = styled.div`
  ${mediaQueryEqualOrUpper('md')(css`
    min-width: 40rem;
  `)}
`;

function CompactSelectorRoot<Item extends LabelItem>({
  className,
  isDisabled = false,
  isReadonly = false,
  onSelectedItemChanged,
  selectedValue = null,
  hasClearButton = true,
  hasBorder = false,
  renderContent,
  tooltip,
  e2ePrefix = 'client',
  autoOpen,
  /**
   * renderInlineWhenOpened on CompactSelector forces the dropdown content to stay
   * in normal DOM flow instead of being mounted inside a Popover (likely via a portal
   * and conditional unmount). That inline mode is a pragmatic workaround for cases
   * where the portal or unmount/remount cycle breaks focus continuity or resets
   * internal controlled state.
   */
  renderInlineWhenOpened = false,
  renderButton,
  popupPlacement,
  onOpenChange,
  // popupArrow,
  // popupOffset,
}: CompactSelectorProps<Item>): React.JSX.Element {
  const [isOpened, setIsOpened] = useState(autoOpen ?? false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const changeOpen = useCallback(
    (isOpen: boolean) => {
      setIsOpened(isOpen);
      onOpenChange?.(isOpen);
    },
    [onOpenChange],
  );

  const toggle = () => changeOpen(!isOpened);

  const onSelected = useCallback(
    (value: any) => {
      changeOpen(false);
      onSelectedItemChanged?.(value);
    },
    [onSelectedItemChanged, changeOpen],
  );

  const onClear = () => onSelected(null);

  const dropdownContent = (
    <StyledCardDiv className="card shadow text-start mt-2" data-e2e={`${e2ePrefix}-selector-dropdown-popup`}>
      <div className="card-body p-3">{renderContent(onSelected, selectedValue)}</div>
    </StyledCardDiv>
  );
  const triggerButton = (
    <OverflowTrigger<HTMLButtonElement>
      onOverflow={setHasOverflow}
      direction="horizontal"
      className="h-100 d-flex flex-grow-1 text-truncate"
      render={ref => renderButton(ref, toggle)}
    />
  );
  const popover = isReadonly ? (
    triggerButton
  ) : (
    <Popover
      open={isOpened && !isDisabled}
      maxWidth={250}
      placement={popupPlacement ?? 'bottom'}
      // offset={popupOffset}
      // arrow={popupArrow}
      style={{ padding: 0 }}
      contents={dropdownContent}
      onOpenChange={changeOpen}
    >
      {triggerButton}
    </Popover>
  );
  const compactSelectorContent = (
    <>
      {renderInlineWhenOpened ? (
        <div className="w-100">
          {triggerButton}
          {isOpened && !isDisabled && dropdownContent}
        </div>
      ) : (
        popover
      )}

      {selectedValue && hasClearButton && (
        <button
          data-e2e={`${e2ePrefix}-selector-unselect-button`}
          className={classNames('btn btn-flat-info ', className)}
          onClick={onClear}
          disabled={isDisabled}
        >
          <i className="icon icon-sm">cancel</i>
        </button>
      )}
    </>
  );
  return (
    <div data-e2e={`${e2ePrefix}-selector`} className={classNames('d-flex min-width-0', { border: hasBorder })}>
      {hasOverflow && tooltip ? (
        <Tooltip position="top" contents={tooltip} color="info">
          <div className="d-flex w-100">{compactSelectorContent}</div>
        </Tooltip>
      ) : (
        compactSelectorContent
      )}
      {!renderInlineWhenOpened && isOpened && <Backdrop onClick={() => toggle()} />}
    </div>
  );
}

export const CompactSelector = Object.assign(CompactSelectorRoot, { ToggleButton });
